/**
 * DonutChart - Simple SVG donut chart for percentage display
 *
 * Atomic level: Atom
 *
 * Displays a percentage as a donut chart with the value in the center.
 * Following GO UI Design System patterns.
 */

import React from 'react';
import { cn } from '../../../../lib/utils';

export interface DonutChartProps {
  /** Percentage value (0-100) */
  percentage: number;
  /** Color for the filled portion */
  color: string;
  /** Size of the chart in pixels */
  size?: number;
  /** Stroke width */
  strokeWidth?: number;
  /** Additional CSS classes */
  className?: string;
}

const DonutChart = React.forwardRef<HTMLDivElement, DonutChartProps>(
  ({ percentage, color, size = 64, strokeWidth = 6, className }, ref) => {
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const strokeDashoffset = circumference - (percentage / 100) * circumference;
    const center = size / 2;

    return (
      <div ref={ref} className={cn('relative inline-flex', className)}>
        <svg width={size} height={size} className="-rotate-90">
          {/* Background circle */}
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth={strokeWidth}
            className="text-muted"
          />
          {/* Foreground circle (percentage) */}
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            className="transition-all duration-500"
          />
        </svg>
        {/* Center text */}
        <div className="absolute inset-0 flex items-center justify-center">
          <span
            className="text-lg font-extrabold tabular-nums"
            style={{ color }}
          >
            {Math.round(percentage)}%
          </span>
        </div>
      </div>
    );
  }
);

DonutChart.displayName = 'DonutChart';

export { DonutChart };
